import {
  ERRORS,
  LOCALE_EN_GB
} from "./chunk-KA7SO5SJ.js";

// src/components/Questionnaire/validation/question/getErrorMessage.ts
var getErrorMessage = ({
  validationMessage,
  questionErrorMessage,
  fallbackMessage
}) => {
  return validationMessage || questionErrorMessage || fallbackMessage;
};

// src/components/Questionnaire/validation/question/rules/dateRange.ts
var dateRange = ({
  name,
  answer,
  validation,
  questionErrorMessage
}) => {
  const { futureOnly, minDays, maxDays } = validation.value;
  const invalid = () => ({
    id: name,
    valid: false,
    message: getErrorMessage({
      validationMessage: validation.message,
      questionErrorMessage
    })
  });
  if (futureOnly) {
    const today = (/* @__PURE__ */ new Date()).toISOString().split("T")[0];
    const date = new Date(answer).toISOString().split("T")[0];
    if (date < today) {
      return invalid();
    }
  }
  if (minDays) {
    const today = /* @__PURE__ */ new Date();
    const date = new Date(answer);
    const minDate = new Date(today.setDate(today.getDate() + minDays));
    if (date < minDate) {
      return invalid();
    }
  }
  if (maxDays) {
    const today = /* @__PURE__ */ new Date();
    const date = new Date(answer);
    const maxDate = new Date(today.setDate(today.getDate() + maxDays));
    if (date > maxDate) {
      return invalid();
    }
  }
  return {
    id: name,
    valid: true
  };
};

// src/components/Questionnaire/validation/question/rules/regex.ts
var regex = ({
  name,
  answer,
  validation,
  questionErrorMessage,
  fallbackMessage
}) => {
  const expression = validation.value;
  const valid = new RegExp(expression).test(answer);
  return {
    id: name,
    valid,
    message: valid ? void 0 : getErrorMessage({
      validationMessage: validation.message,
      questionErrorMessage,
      fallbackMessage
    })
  };
};
var caseInsensitiveRegex = ({
  name,
  answer,
  validation,
  questionErrorMessage,
  fallbackMessage
}) => {
  const expression = validation.value;
  const valid = new RegExp(expression, "i").test(answer);
  return {
    id: name,
    valid,
    message: valid ? void 0 : getErrorMessage({
      validationMessage: validation.message,
      questionErrorMessage,
      fallbackMessage
    })
  };
};

// src/components/Questionnaire/validation/question/rules/email.ts
var email = ({ name, answer, questionErrorMessage }) => {
  const expression = "^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@(?!xn--)[a-z0-9](?:[a-z0-9_-]{0,61}[a-z0-9])?(?:\\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])){1,}$";
  return caseInsensitiveRegex({
    name,
    answer,
    validation: {
      type: "regex",
      value: expression
    },
    questionErrorMessage,
    fallbackMessage: ERRORS.INVALID_EMAIL
  });
};

// src/components/Questionnaire/validation/question/rules/postcode.ts
var postcode = ({ name, answer, questionErrorMessage }) => {
  const expression = "^\\s*([A-Z]{1,2}[0-9R][0-9A-Z]?)\\s*(([0-9])([ABDEFGHJLNPQRSTUWXYZ]{2}))\\s*$";
  return caseInsensitiveRegex({
    name,
    answer,
    validation: {
      type: "regex",
      value: expression
    },
    questionErrorMessage,
    fallbackMessage: ERRORS.INVALID_POSTCODE
  });
};

// src/components/Questionnaire/validation/question/rules/list.ts
var list = ({
  name,
  answer,
  validation,
  questionErrorMessage
}) => {
  const allowedValues = validation.value;
  const valid = allowedValues.includes(answer);
  return {
    id: name,
    valid,
    message: valid ? void 0 : getErrorMessage({
      validationMessage: validation.message,
      questionErrorMessage
    })
  };
};

// src/components/Questionnaire/validation/question/rules/percentages.ts
var percentages = ({
  name,
  answer,
  validation,
  questionErrorMessage
}) => {
  let valid = true;
  const rangeConfig = validation.value;
  const individualMin = Number(rangeConfig?.min) || 0;
  const individualMax = Number(rangeConfig?.max) || 100;
  if (!answer) {
    valid = false;
  } else {
    const parts = answer.split(",").map((p) => p.trim()).filter(Boolean).map(Number);
    if (!parts.length) {
      valid = false;
    } else {
      let total = 0;
      for (const num of parts) {
        if (!Number.isFinite(num) || num < 0) {
          valid = false;
          break;
        }
        if (num > 0 && (num < individualMin || num > individualMax)) {
          valid = false;
          break;
        }
        total += num;
      }
      if (valid && total !== 100) {
        valid = false;
      }
    }
  }
  return {
    id: name,
    valid,
    message: valid ? void 0 : getErrorMessage({
      validationMessage: validation.message,
      questionErrorMessage,
      fallbackMessage: "Percentages must total 100%"
    })
  };
};

// src/components/Questionnaire/validation/question/rules/range.ts
var range = ({
  name,
  answer,
  validation,
  questionErrorMessage
}) => {
  const { min, max } = validation.value;
  let answerValue = answer;
  let valid = false;
  if (typeof min === "number" && typeof max === "number") {
    answerValue = Number(answer);
  }
  valid = (answerValue >= min ? true : false) && (answerValue <= max ? true : false);
  return {
    id: name,
    valid,
    message: valid ? void 0 : getErrorMessage({
      validationMessage: validation.message,
      questionErrorMessage
    })
  };
};

// src/components/Questionnaire/validation/question/rules/tel.ts
var tel = ({
  name,
  answer,
  validation,
  locale = LOCALE_EN_GB,
  questionErrorMessage
}) => {
  const ukPhoneRegex = /^(\+44|0044|44|0)\d{9,11}$/;
  const usPhoneRegex = /^\(\d{3}\)\s\d{3}-\d{4}$/;
  const strippedAnswer = answer.replace(/\s/g, "");
  const isUK = locale === LOCALE_EN_GB;
  const valid = isUK ? ukPhoneRegex.test(strippedAnswer) : usPhoneRegex.test(answer);
  return {
    id: name,
    valid,
    message: valid ? void 0 : getErrorMessage({
      validationMessage: validation.message,
      questionErrorMessage,
      fallbackMessage: ERRORS.INVALID_TEL
    })
  };
};

// src/components/Questionnaire/validation/question/rules/zipcode.ts
var zipcode = ({ name, answer, questionErrorMessage }) => {
  const expression = "^[0-9]{5}$";
  return regex({
    name,
    answer,
    validation: {
      type: "regex",
      value: expression
    },
    questionErrorMessage,
    fallbackMessage: ERRORS.INVALID_ZIPCODE
  });
};

// src/components/Questionnaire/validation/question/rules/index.ts
var ok = (name) => ({
  id: name,
  valid: true,
  message: void 0
});
var validators = {
  range,
  list,
  regex,
  email,
  postcode,
  tel,
  date: dateRange,
  percentages,
  zipcode
};
var applyRules = ({
  name,
  validation,
  answer,
  locale,
  questionErrorMessage
}) => {
  if (!answer) {
    return {
      id: name,
      valid: false,
      message: getErrorMessage({
        questionErrorMessage,
        fallbackMessage: ERRORS.REQUIRED
      })
    };
  }
  const { type } = validation;
  if (!(type in validators)) {
    return ok(name);
  }
  const validator = validators[type];
  return validator({ name, validation, answer, locale, questionErrorMessage });
};

// src/components/validation/question/utils.ts
var buildPercentagesAnswer = (name, answers) => {
  const prefix = `${name}[`;
  const parts = [];
  for (const [k, v] of Object.entries(answers)) {
    if (k.startsWith(prefix) && k.endsWith("]")) {
      const num = Number(v);
      if (Number.isFinite(num) && num >= 0) {
        parts.push(num);
      }
    }
  }
  return parts.join(",");
};
var buildPercentagesAnswerForGroup = (name, groups, answers) => {
  const group = groups[name];
  if (!group || group.kind !== "percent") {
    return "";
  }
  const regularQuestionNames = group.names.slice(0, -1);
  const parts = [];
  for (const questionName of regularQuestionNames) {
    const value = answers[questionName];
    if (value === void 0 || value === null || value === "") {
      continue;
    }
    const num = Number(String(value).replace(/%/g, ""));
    if (Number.isFinite(num) && num >= 0) {
      parts.push(num);
    }
  }
  return parts.join(",");
};

// src/components/validation/question/validate.ts
var inferredValidationList = ["tel", "email", "postcode", "zipcode"];
var validateQuestion = ({
  question,
  answers,
  locale,
  groups
}) => {
  const { name, validation, visible, display } = question;
  let inferredValidation = validation;
  if (display?.type && inferredValidationList.includes(display?.type)) {
    inferredValidation = { type: display.type };
  }
  if (!inferredValidation || !visible) {
    return ok(name);
  }
  let answer = (answers[name] ?? "").toString();
  if (question.validation?.type === "percentages") {
    answer = display?.type === "percentage_split_group" && groups ? buildPercentagesAnswerForGroup(name, groups, answers) : buildPercentagesAnswer(name, answers);
  }
  const result = applyRules({
    name,
    validation: inferredValidation,
    answer,
    locale,
    questionErrorMessage: question.errorMessage
  });
  return result ?? ok(name);
};

export {
  getErrorMessage,
  ok,
  validateQuestion
};
